import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { TCMLogo } from "../components/TCMLogo";
import { ClientDetailBlock } from "../components/ClientDetailBlock";

const DEST_TIME = () =>
  new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function AdminDashboard() {
  const { API, authHeaders, user, logout, token } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [view, setView] = useState("dashboard"); // "dashboard" | "directory" | "financials"

  const load = () => axios.get(`${API}/admin/dashboard`, { headers: authHeaders }).then((r) => setDash(r.data));
  useEffect(() => {
    load();
  }, []);

  if (!dash) return <p className="p-10 tcm-eyebrow">Loading...</p>;
  const buckets = dash.trips_by_bucket;

  const doCta = async (item) => {
    try {
      if (item.kind === "paid_claim") {
        await axios.post(`${API}/budget/${item.budget_line_id}/mark-paid`, {}, { headers: authHeaders });
        toast.success("Marked paid");
      } else if (item.kind === "pending_booking") {
        await axios.post(`${API}/reservations/${item.reservation_id}/confirm`, {}, { headers: authHeaders });
        toast.success("Confirmed");
      } else if (item.kind === "not_logged_in") {
        await axios.post(`${API}/admin/users/${item.user_id}/resend-invite`, {}, { headers: authHeaders });
        toast.success("Invite resent");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const attentionKey = (it) => {
    if (it.kind === "paid_claim" || it.kind === "payment_due") return `${it.kind}:${it.budget_line_id}`;
    if (it.kind === "pending_booking") return `${it.kind}:${it.reservation_id}`;
    if (it.kind === "not_logged_in") return `${it.kind}:${it.user_id}`;
    return `${it.kind}:${it.trip_id || ""}`;
  };

  const dismissAttention = async (it) => {
    try {
      await axios.post(`${API}/admin/dashboard/dismiss`, { key: attentionKey(it) }, { headers: authHeaders });
      toast.success("Dismissed");
      load();
    } catch { toast.error("Failed"); }
  };

  const clearAllAttention = async () => {
    if (!window.confirm("Dismiss every item currently in Needs attention? Items will reappear if their condition is still true after a refresh.")) return;
    try {
      await axios.post(`${API}/admin/dashboard/dismiss-all`, {}, { headers: authHeaders });
      toast.success("All dismissed");
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteActivity = async (aid) => {
    try {
      await axios.delete(`${API}/admin/activities/${aid}`, { headers: authHeaders });
      load();
    } catch { toast.error("Failed"); }
  };

  const clearAllActivity = async () => {
    if (!window.confirm("Clear the entire Recent activity feed? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/activities`, { headers: authHeaders });
      toast.success("Activity cleared");
      load();
    } catch { toast.error("Failed"); }
  };

  const timeAgo = (iso) => {
    try {
      const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
      if (diff < 1) return "just now";
      if (diff < 60) return `${diff} min ago`;
      if (diff < 60 * 24) return `${Math.floor(diff / 60)} h ago`;
      const d = new Date(iso);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="border-b border-[color:var(--tcm-navy-15)] px-8 py-4 flex items-center gap-4 bg-[color:var(--tcm-cream)]">
        <div className="flex-1 flex items-center gap-5">
          <TCMLogo size="md" />
          <span className="tcm-eyebrow">Portal Management</span>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="new-client-btn" onClick={() => nav("/admin/clients/new")} className="tcm-btn-outline text-[11px] py-2 px-3">
            Invite client
          </button>
          <button data-testid="new-trip-btn" onClick={() => nav("/admin/trips/new")} className="tcm-btn-primary text-[11px] py-2 px-3">
            New trip
          </button>
          <button data-testid="print-queue-btn" onClick={() => nav("/admin/print-queue")} className="tcm-btn-outline text-[11px] py-2 px-3">
            Print queue
          </button>
          <button data-testid="templates-btn" onClick={() => nav("/admin/templates")} className="tcm-btn-outline text-[11px] py-2 px-3">
            Templates
          </button>
          {user?.role === "admin" && (
            <button data-testid="team-btn" onClick={() => nav("/admin/team")} className="tcm-btn-outline text-[11px] py-2 px-3">
              Team
            </button>
          )}
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/"); }} className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
            Sign out
          </button>
        </div>
      </div>

      <main className="px-8 lg:px-11 py-9 max-w-[1400px] mx-auto">
        {/* Header */}
        <div>
          <p className="tcm-eyebrow">{DEST_TIME()}</p>
          <h1 className="text-[40px] font-normal mt-1.5">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, {user?.full_name?.split(" ")[0]}
          </h1>
        </div>

        {/* View switcher */}
        <nav className="mt-7 mb-10 flex items-center gap-1 border-b border-[color:var(--tcm-navy-15)]" data-testid="admin-tabs">
          {[
            { k: "dashboard",  label: "Dashboard" },
            { k: "directory",  label: "Client directory" },
            { k: "financials", label: "Financials" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setView(t.k)}
              className="px-4 py-3 text-[11px] tracking-[0.2em] uppercase relative transition-colors"
              style={{
                color: view === t.k ? "var(--tcm-navy)" : "var(--tcm-navy-70)",
                fontWeight: view === t.k ? 500 : 400,
              }}>
              {t.label}
              {view === t.k && (
                <span className="absolute left-0 right-0 -bottom-px h-[2px]"
                      style={{ background: "var(--tcm-gold)" }} />
              )}
            </button>
          ))}
        </nav>

        {view === "dashboard" && (
        <div className="space-y-14">
        {/* Trip columns + Needs attention/activity */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] gap-9 items-start">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px]">
            <TripColumn label="Planning" trips={buckets.planning} live={false} nav={nav} />
            <TripColumn label="Live" trips={buckets.live} live={true} nav={nav} />
            <TripColumn label="Completed" trips={buckets.completed} live={false} nav={nav} />
          </div>

          <div className="flex flex-col gap-7">
            <section>
              <div className="flex justify-between items-baseline pb-2 border-b border-[color:var(--tcm-gold)]">
                <h2 className="text-[24px] font-medium m-0" data-testid="attention-title">Needs attention</h2>
                <div className="flex items-center gap-3">
                  {dash.needs_attention.length > 0 && (
                    <button data-testid="attention-clear-all" onClick={clearAllAttention}
                            className="text-[10.5px] tracking-[0.14em] uppercase underline decoration-[#C9A961] underline-offset-4">
                      Clear all
                    </button>
                  )}
                  <span className="font-display text-[20px]">{dash.needs_attention.length}</span>
                </div>
              </div>
              {dash.needs_attention.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-4">All clear.</p>}
              {dash.needs_attention.map((it, i) => (
                <div key={i} data-testid={`attention-${i}`} className="flex gap-3.5 items-center py-3 border-b border-[color:var(--tcm-navy-15)]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-[10.5px] tracking-[0.12em] uppercase" style={{ color: "var(--tcm-gold-700)" }}>{it.eyebrow}</div>
                      {it.segment === "vip" && (
                        <span data-testid={`attention-vip-${i}`}
                              className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-[1px]"
                              style={{ background: "var(--tcm-navy)", color: "var(--tcm-cream)" }}>VIP</span>
                      )}
                      {it.segment === "repeat" && (
                        <span className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-[1px] border"
                              style={{ borderColor: "var(--tcm-gold)", color: "var(--tcm-gold-700)" }}>Repeat</span>
                      )}
                    </div>
                    <div className="text-[13.5px] leading-[1.45] mt-0.5">{it.text}</div>
                  </div>
                  {it.cta && (
                    <button data-testid={`attention-cta-${i}`} onClick={() => doCta(it)} className="tcm-btn-primary text-[11px] py-2 px-3 whitespace-nowrap">
                      {it.cta}
                    </button>
                  )}
                  <button data-testid={`attention-dismiss-${i}`} onClick={() => dismissAttention(it)}
                          title="Dismiss — hide this item without actioning it"
                          className="w-8 h-8 flex items-center justify-center text-[color:var(--tcm-navy-70)] hover:text-[color:var(--tcm-navy)] hover:bg-[color:var(--tcm-navy-15)]/50 transition-colors text-[16px] leading-none">
                    ×
                  </button>
                </div>
              ))}
            </section>

            <section>
              <div className="flex justify-between items-baseline pb-2 border-b border-[color:var(--tcm-gold)]">
                <h2 className="text-[24px] font-medium m-0">Recent client activity</h2>
                {dash.recent_activity.length > 0 && (
                  <button data-testid="activity-clear-all" onClick={clearAllActivity}
                          className="text-[10.5px] tracking-[0.14em] uppercase underline decoration-[#C9A961] underline-offset-4">
                    Clear all
                  </button>
                )}
              </div>
              {dash.recent_activity.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-4">Nothing yet.</p>}
              {dash.recent_activity.map((a) => (
                <div key={a.id} data-testid={`activity-row-${a.id}`}
                     className="flex justify-between gap-3 py-2.5 border-b border-[color:var(--tcm-navy-15)] text-[13.5px] items-center group">
                  <span className="flex-1 min-w-0">{a.detail}</span>
                  <span className="text-[color:var(--tcm-navy-70)] whitespace-nowrap">{timeAgo(a.at)}</span>
                  <button data-testid={`activity-del-${a.id}`} onClick={() => deleteActivity(a.id)}
                          title="Remove from feed"
                          className="w-6 h-6 flex items-center justify-center text-[color:var(--tcm-navy-70)] hover:text-[color:var(--tcm-navy)] hover:bg-[color:var(--tcm-navy-15)]/50 transition-colors text-[14px] leading-none opacity-0 group-hover:opacity-100">
                    ×
                  </button>
                </div>
              ))}
            </section>
          </div>
        </div>
        </div>
        )}

        {view === "directory" && <ClientDirectorySection />}
        {view === "financials" && <FinancialsSection />}
      </main>
    </div>
  );
}

function TripColumn({ label, trips, live, nav }) {
  return (
    <div className="flex flex-col gap-2.5" data-testid={`col-${label.toLowerCase()}`}>
      <div className="flex justify-between items-baseline pb-2 border-b border-[color:var(--tcm-gold)]">
        <span className="text-[11px] tracking-[0.16em] uppercase" style={{ color: "var(--tcm-gold-700)" }}>{label}</span>
        <span className="font-display text-[20px] tabular-nums">{trips.length}</span>
      </div>
      {trips.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-3">—</p>}
      {trips.map((t) => (
        <button
          key={t.id}
          data-testid={`admin-trip-${t.id}`}
          onClick={() => nav(`/admin/trips/${t.id}`)}
          className="tcm-card p-3.5 gap-1 text-left cursor-pointer hover:opacity-90"
          style={live ? { boxShadow: "inset 0 0 0 3px var(--tcm-cream), inset 0 0 0 4px var(--tcm-gold)" } : {}}>
          <span className="font-display text-[19px] leading-[1.15]">{t.destination}</span>
          <span className="text-[12.5px] text-[color:var(--tcm-navy-70)]">{t.client?.full_name}</span>
          <span className="text-[12px] text-[color:var(--tcm-navy-70)] tabular-nums">{t.start_date} – {t.end_date}</span>
          <span className="text-[11px] text-[color:var(--tcm-navy-70)] flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            <span>{t.party_size || 1} guest{(t.party_size || 1) > 1 ? "s" : ""}</span>
            <span>·</span>
            <span>Owner: {(t.staff || []).map((s) => s.full_name.split(" ")[0]).join(", ") || "Unassigned"}</span>
          </span>
          {live && t.day_counter && (
            <span className="tcm-tag-accent self-start mt-1.5">Day {t.day_counter.day} of {t.day_counter.total}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// -------------- Client Directory --------------
function ClientDirectorySection() {
  const { API, authHeaders, user: currentUser } = useAuth();
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [segment, setSegment] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [drawerTripId, setDrawerTripId] = useState(null);

  const load = () => {
    axios
      .get(`${API}/admin/clients-directory?status=${filter}&segment=${segment}&q=${encodeURIComponent(q)}`, { headers: authHeaders })
      .then((r) => setRows(r.data.clients || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, segment]);

  const setClientSegment = async (userId, seg) => {
    try {
      await axios.post(`${API}/admin/clients/${userId}/segment`,
        { segment: seg }, { headers: authHeaders });
      toast.success(seg ? "Segment pinned" : "Auto segment restored");
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteClient = async (userId, name) => {
    const confirm1 = window.prompt(
      `Permanently delete ${name} and every trip, document, diary entry and message they own?\n\nType DELETE to confirm.`
    );
    if (confirm1 !== "DELETE") return;
    try {
      await axios.delete(`${API}/admin/clients/${userId}`, { headers: authHeaders });
      toast.success(`${name} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <section data-testid="client-directory">
      <div className="flex justify-between items-baseline pb-2 border-b border-[color:var(--tcm-gold)] flex-wrap gap-3">
        <div>
          <p className="tcm-eyebrow">Directory</p>
          <h2 className="text-[24px] font-medium m-0">Client directory</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { k: "all", label: "All" },
            { k: "live", label: "Live" },
            { k: "upcoming", label: "Upcoming" },
            { k: "no_trip", label: "No trip yet" },
            { k: "past", label: "Past" },
          ].map((b) => (
            <button
              key={b.k}
              data-testid={`filter-${b.k}`}
              onClick={() => setFilter(b.k)}
              className={`text-[11px] tracking-[0.16em] uppercase py-2 px-3 border ${filter === b.k ? "bg-[color:var(--tcm-navy)] text-[color:var(--tcm-cream)] border-[color:var(--tcm-navy)]" : "border-[color:var(--tcm-navy-15)] text-[color:var(--tcm-navy)]"}`}>
              {b.label}
            </button>
          ))}
          <input
            data-testid="directory-search"
            className="tcm-input py-2 px-3 max-w-[220px] text-[13px]"
            placeholder="Search client or destination"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
          <button data-testid="directory-search-btn" onClick={load} className="tcm-btn-outline text-[11px] py-2 px-3">Search</button>
        </div>
      </div>

      {/* Segment sub-filter */}
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <span className="tcm-eyebrow">Segment</span>
        {[
          { k: "all", label: "All" },
          { k: "vip", label: "VIP" },
          { k: "repeat", label: "Repeat" },
          { k: "first_time", label: "First-time" },
        ].map((s) => (
          <button
            key={s.k}
            data-testid={`seg-filter-${s.k}`}
            onClick={() => setSegment(s.k)}
            className={`text-[10.5px] tracking-[0.14em] uppercase py-1.5 px-2.5 border ${segment === s.k ? "bg-[color:var(--tcm-gold)] text-[color:var(--tcm-navy)] border-[color:var(--tcm-gold)]" : "border-[color:var(--tcm-navy-15)] text-[color:var(--tcm-navy)]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-[color:var(--tcm-navy-70)] py-6">No trips match.</p>
      ) : (
        <table className="w-full mt-4 text-[13.5px]">
          <thead>
            <tr className="text-left tcm-eyebrow">
              <th className="py-2 pr-3">Client</th>
              <th className="py-2 pr-3">Segment</th>
              <th className="py-2 pr-3">Destination</th>
              <th className="py-2 pr-3">Dates</th>
              <th className="py-2 pr-3">Guests</th>
              <th className="py-2 pr-3">Staff owner</th>
              <th className="py-2 pr-3 text-right">Planning status</th>
              {currentUser?.role === "admin" && <th className="py-2 pr-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const noTrip = r.bucket === "no_trip";
              const out = r.outstanding?.total || 0;
              const status = noTrip ? "newcomer" : (out === 0 ? "clear" : out <= 2 ? "amber" : "red");
              const color = status === "clear" ? "var(--tcm-navy)"
                          : status === "amber" ? "var(--tcm-gold-700)"
                          : status === "newcomer" ? "var(--tcm-gold-700)"
                          : "#8B2E2E";
              const rowKey = r.trip_id || `client-${r.client?.id}`;
              return (
                <tr
                  key={rowKey}
                  data-testid={`directory-row-${rowKey}`}
                  onClick={() => r.trip_id ? setDrawerTripId(r.trip_id) : nav(`/admin/trips/new?client_id=${r.client?.id}`)}
                  className="border-b border-[color:var(--tcm-navy-15)] cursor-pointer hover:bg-[color:var(--tcm-navy)]/[0.03]">
                  <td className="py-3 pr-3">
                    <span className="font-display text-[16px]">{r.client?.full_name || "—"}</span>
                    <span className="block text-[11px] text-[color:var(--tcm-navy-70)]">{r.client?.email}</span>
                    {r.invited_not_logged_in && (
                      <span className="inline-block mt-1 text-[9px] tracking-[0.14em] uppercase px-1.5 py-[1px] border"
                            style={{ borderColor: "#8B2E2E", color: "#8B2E2E" }}>Invite pending</span>
                    )}
                  </td>
                  <td className="py-3 pr-3" onClick={(e) => e.stopPropagation()}>
                    <SegmentPill segment={r.segment} manual={r.segment_manual}
                                  userId={r.client?.id} onChange={setClientSegment} />
                  </td>
                  <td className="py-3 pr-3">{r.destination || <span className="opacity-50">— no trip yet —</span>}</td>
                  <td className="py-3 pr-3 tabular-nums">{r.start_date ? `${r.start_date} – ${r.end_date}` : "—"}</td>
                  <td className="py-3 pr-3 tabular-nums">{r.party_size || "—"}</td>
                  <td className="py-3 pr-3">{(r.staff || []).map((s) => s.full_name.split(" ")[0]).join(", ") || <span className="opacity-60">—</span>}</td>
                  <td className="py-3 pr-3 text-right">
                    {noTrip ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase px-2.5 py-1"
                            style={{ border: `1px solid ${color}`, color }}>
                        Create trip
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase px-2.5 py-1"
                        style={{ border: `1px solid ${color}`, color }}>
                        <span className="font-display text-[16px] leading-none tabular-nums">{out}</span>
                        <span>{out === 0 ? "clear" : "outstanding"}</span>
                      </span>
                    )}
                  </td>
                  {currentUser?.role === "admin" && (
                    <td className="py-3 pr-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`delete-client-${r.client?.id}`}
                        onClick={() => deleteClient(r.client?.id, r.client?.full_name || "this client")}
                        title="Delete client and all their data"
                        className="text-[10.5px] tracking-[0.14em] uppercase px-2 py-1 border"
                        style={{ borderColor: "#8B2E2E", color: "#8B2E2E" }}>
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {drawerTripId && (
        <DirectoryDrawer tripId={drawerTripId} onClose={() => setDrawerTripId(null)}
                         onExpand={() => { const id = drawerTripId; setDrawerTripId(null); nav(`/admin/clients/${id}`); }} />
      )}
    </section>
  );
}

const SEGMENT_STYLES = {
  vip:        { label: "VIP",        bg: "var(--tcm-navy)",    fg: "var(--tcm-cream)",   border: "var(--tcm-navy)" },
  repeat:     { label: "Repeat",     bg: "transparent",         fg: "var(--tcm-navy)",    border: "var(--tcm-gold)" },
  first_time: { label: "First-time", bg: "transparent",         fg: "var(--tcm-navy-70)", border: "var(--tcm-navy-15)" },
};

function SegmentPill({ segment, manual, userId, onChange }) {
  const [open, setOpen] = useState(false);
  const style = SEGMENT_STYLES[segment] || SEGMENT_STYLES.first_time;
  if (!userId) {
    return <span className="text-[11px] tracking-[0.12em] uppercase opacity-40">—</span>;
  }
  return (
    <div className="relative inline-block">
      <button
        data-testid={`segment-pill-${userId}`}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.14em] uppercase px-2.5 py-1 border cursor-pointer"
        style={{ background: style.bg, color: style.fg, borderColor: style.border }}
        title={manual ? "Pinned manually — click to change" : "Auto-computed — click to override"}>
        <span>{style.label}</span>
        {manual && <span style={{ color: style.fg }}>·</span>}
        {manual && <span className="text-[9px] opacity-80">pinned</span>}
      </button>
      {open && (
        <div className="absolute top-[110%] right-0 z-30 min-w-[160px] bg-[color:var(--tcm-cream)] border border-[color:var(--tcm-navy)] shadow-lg">
          {["vip", "repeat", "first_time"].map((s) => (
            <button
              key={s}
              data-testid={`segment-set-${userId}-${s}`}
              onClick={() => { onChange(userId, s); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-[11px] tracking-[0.12em] uppercase hover:bg-[color:var(--tcm-navy)] hover:text-[color:var(--tcm-cream)]">
              Pin as {SEGMENT_STYLES[s].label}
            </button>
          ))}
          <button
            data-testid={`segment-clear-${userId}`}
            onClick={() => { onChange(userId, null); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-[11px] tracking-[0.12em] uppercase border-t border-[color:var(--tcm-navy-15)] hover:bg-[color:var(--tcm-navy)] hover:text-[color:var(--tcm-cream)]">
            Use auto
          </button>
        </div>
      )}
    </div>
  );
}

function DirectoryDrawer({ tripId, onClose, onExpand }) {
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="directory-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="w-[560px] max-w-full h-full bg-[color:var(--tcm-cream)] shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 z-10 bg-[color:var(--tcm-cream)] border-b border-[color:var(--tcm-navy-15)] px-6 py-3 flex items-center justify-between">
          <span className="tcm-eyebrow">Client detail</span>
          <div className="flex items-center gap-2">
            <button
              data-testid="drawer-expand"
              onClick={onExpand}
              title="Open full page"
              className="tcm-btn-outline text-[11px] py-1.5 px-3">Expand</button>
            <button data-testid="drawer-close" onClick={onClose} className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">Close</button>
          </div>
        </div>
        <div className="p-6">
          <ClientDetailBlock tripId={tripId} />
        </div>
      </aside>
    </div>
  );
}

// -------------- Financials --------------
function FinancialsSection() {
  const { API, authHeaders, token } = useAuth();
  const [data, setData] = useState(null);
  const [view, setView] = useState("summary"); // "summary" | "ledger"
  const load = () => axios.get(`${API}/admin/financials`, { headers: authHeaders }).then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const markReviewed = async (id) => {
    try {
      await axios.post(`${API}/admin/financials/notifications/${id}/review`, {}, { headers: authHeaders });
      toast.success("Marked reviewed");
      load();
    } catch { toast.error("Failed"); }
  };
  const saveNote = async (id, note) => {
    try {
      await axios.post(`${API}/admin/financials/notifications/${id}/note`,
        { note }, { headers: authHeaders });
      load();
    } catch { toast.error("Failed to save note"); }
  };

  if (!data) return <p className="p-6 tcm-eyebrow">Loading financials…</p>;
  const fmt = (cur, n) => `${cur} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <section data-testid="financials-section" className="space-y-6">
      <div className="flex justify-between items-baseline pb-2 border-b border-[color:var(--tcm-gold)] flex-wrap gap-3">
        <div>
          <p className="tcm-eyebrow">Financials</p>
          <h2 className="text-[24px] font-medium m-0">Portal financials</h2>
        </div>
        <div className="flex items-center gap-2">
          {[
            { k: "summary", label: "Summary" },
            { k: "ledger",  label: "Ledger"  },
          ].map((v) => (
            <button
              key={v.k}
              data-testid={`fin-view-${v.k}`}
              onClick={() => setView(v.k)}
              className={`text-[11px] tracking-[0.16em] uppercase py-2 px-3 border ${view === v.k ? "bg-[color:var(--tcm-navy)] text-[color:var(--tcm-cream)] border-[color:var(--tcm-navy)]" : "border-[color:var(--tcm-navy-15)] text-[color:var(--tcm-navy)]"}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === "summary" ? (
        <>
          {/* Global totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["EUR", "GBP"].map((cur) => (
              <React.Fragment key={cur}>
                <TotalTile label={`Total booked (${cur})`} value={fmt(cur, data.totals[cur].total)} testid={`total-${cur}`} />
                <TotalTile label={`Outstanding (${cur})`} value={fmt(cur, data.totals[cur].outstanding)}
                           accent={data.totals[cur].outstanding > 0} testid={`outstanding-${cur}`} />
              </React.Fragment>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Trips outstanding */}
            <div data-testid="financials-trips">
              <p className="text-[11px] tracking-[0.16em] uppercase mb-2" style={{ color: "var(--tcm-gold-700)" }}>By trip</p>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left tcm-eyebrow">
                    <th className="py-2 pr-3">Trip</th>
                    <th className="py-2 pr-3 text-right">Paid</th>
                    <th className="py-2 pr-3 text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trips.map((t) => (
                    <tr key={t.trip_id} className="border-b border-[color:var(--tcm-navy-15)]">
                      <td className="py-2 pr-3">
                        <span className="font-display text-[15px]">{t.destination}</span>
                        <span className="block text-[11px] text-[color:var(--tcm-navy-70)]">{t.client_name}</span>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmt(t.currency, t.paid)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums font-medium" style={{ color: t.outstanding > 0 ? "var(--tcm-gold-700)" : "inherit" }}>
                        {fmt(t.currency, t.outstanding)}
                      </td>
                    </tr>
                  ))}
                  {data.trips.length === 0 && <tr><td colSpan={3} className="py-6 text-sm text-[color:var(--tcm-navy-70)]">No trips.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Recent payments */}
            <div data-testid="financials-recent">
              <p className="text-[11px] tracking-[0.16em] uppercase mb-2" style={{ color: "var(--tcm-gold-700)" }}>Recent payments</p>
              {data.recent_payments.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-4">No recent payments.</p>}
              <ul className="space-y-2">
                {data.recent_payments.map((p) => (
                  <li key={p.id} className="flex justify-between gap-3 border-b border-[color:var(--tcm-navy-15)] pb-2 text-[13px]">
                    <div>
                      <div className="font-display text-[15px]">{p.label}</div>
                      <div className="text-[11px] text-[color:var(--tcm-navy-70)]">{p.client_name} · {p.destination} · {p.method}</div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="tabular-nums">{fmt(p.currency, p.amount)}</div>
                      <div className="text-[10px] text-[color:var(--tcm-navy-70)]">{p.paid_at ? new Date(p.paid_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</div>
                      <a data-testid={`recent-receipt-${p.id}`}
                         href={`${API}/budget/${p.id}/payment-receipt.pdf?auth=${token}`}
                         target="_blank" rel="noreferrer"
                         className="text-[10px] tracking-[0.14em] uppercase underline decoration-[#C9A961] underline-offset-4 text-[color:var(--tcm-gold-700)]">
                        PDF
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Vendor notifications */}
          <div data-testid="financials-notifications">
            <p className="text-[11px] tracking-[0.16em] uppercase mb-2" style={{ color: "var(--tcm-gold-700)" }}>Payment notifications</p>
            {data.notifications.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-4">Nothing yet. Notifications appear here when a client makes a payment.</p>}
            <ul className="space-y-2">
              {data.notifications.map((n) => (
                <NotificationRow key={n.id} n={n} fmt={fmt} onReview={markReviewed} onSaveNote={saveNote} />
              ))}
            </ul>
          </div>
        </>
      ) : (
        <FinancialsLedger fmt={fmt} onSaveNote={saveNote} onReview={markReviewed} />
      )}
    </section>
  );
}

function NotificationRow({ n, fmt, onReview, onSaveNote }) {
  const [note, setNote] = useState(n.note || "");
  const [showNote, setShowNote] = useState(!!n.note);
  return (
    <li data-testid={`notif-${n.id}`}
        className={`flex flex-col gap-2 border-b border-[color:var(--tcm-navy-15)] py-3 text-[13px] ${n.reviewed ? "opacity-75" : ""}`}>
      <div className="flex justify-between gap-3 items-start">
        <div className="min-w-0">
          <div className="text-[10.5px] tracking-[0.12em] uppercase" style={{ color: "var(--tcm-gold-700)" }}>
            {n.kind === "vendor" ? "Vendor receipt uploaded" : "TCM Stripe payment"}
            {n.reviewed && <span className="ml-2 opacity-60">· Reviewed</span>}
          </div>
          <div className="font-display text-[15px] truncate">
            {n.client_name} · {n.supplier || n.label}
          </div>
          <div className="text-[11px] text-[color:var(--tcm-navy-70)]">
            {fmt(n.currency, n.amount)} · {n.at ? new Date(n.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`notif-note-toggle-${n.id}`} onClick={() => setShowNote(!showNote)}
                  className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4 whitespace-nowrap">
            {showNote ? "Hide note" : (n.note ? "Edit note" : "Add note")}
          </button>
          {!n.reviewed && (
            <button data-testid={`notif-review-${n.id}`} onClick={() => onReview(n.id)} className="tcm-btn-outline text-[11px] py-2 px-3 whitespace-nowrap">
              Mark reviewed
            </button>
          )}
        </div>
      </div>
      {showNote && (
        <div className="pl-1">
          <textarea
            data-testid={`notif-note-input-${n.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => note !== (n.note || "") && onSaveNote(n.id, note)}
            placeholder="Private note · e.g. reconciled with Q4 books"
            className="tcm-input text-[13px] py-2 px-3 w-full"
            rows={2}
            maxLength={500}
          />
          {n.note_by_name && n.note_at && (
            <p className="text-[10px] tracking-[0.1em] uppercase mt-1 opacity-60">
              Last saved by {n.note_by_name} · {new Date(n.note_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function FinancialsLedger({ fmt, onSaveNote, onReview }) {
  const { API, authHeaders } = useAuth();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ reviewed: "all", kind: "all" });
  const load = () => {
    axios.get(`${API}/admin/financials/ledger?reviewed=${filters.reviewed}&kind=${filters.kind}`, { headers: authHeaders })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  return (
    <div data-testid="financials-ledger" className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="tcm-eyebrow">Filter</span>
        {[
          { k: "reviewed", v: "all", label: "All" },
          { k: "reviewed", v: "no", label: "Open" },
          { k: "reviewed", v: "yes", label: "Reviewed" },
        ].map((f) => (
          <button
            key={`r-${f.v}`}
            data-testid={`ledger-r-${f.v}`}
            onClick={() => setFilters({ ...filters, reviewed: f.v })}
            className={`text-[10.5px] tracking-[0.14em] uppercase py-1.5 px-2.5 border ${filters.reviewed === f.v ? "bg-[color:var(--tcm-navy)] text-[color:var(--tcm-cream)] border-[color:var(--tcm-navy)]" : "border-[color:var(--tcm-navy-15)]"}`}>
            {f.label}
          </button>
        ))}
        <span className="tcm-eyebrow ml-4">Type</span>
        {[
          { v: "all", label: "All" },
          { v: "tcm", label: "TCM" },
          { v: "vendor", label: "Vendor" },
        ].map((f) => (
          <button
            key={`k-${f.v}`}
            data-testid={`ledger-k-${f.v}`}
            onClick={() => setFilters({ ...filters, kind: f.v })}
            className={`text-[10.5px] tracking-[0.14em] uppercase py-1.5 px-2.5 border ${filters.kind === f.v ? "bg-[color:var(--tcm-navy)] text-[color:var(--tcm-cream)] border-[color:var(--tcm-navy)]" : "border-[color:var(--tcm-navy-15)]"}`}>
            {f.label}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[color:var(--tcm-navy-70)] py-6">No entries match.</p>
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left tcm-eyebrow">
              <th className="py-2 pr-3">When</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Client · Purpose</th>
              <th className="py-2 pr-3 text-right">Amount</th>
              <th className="py-2 pr-3">Note</th>
              <th className="py-2 pr-3 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => (
              <LedgerRow key={n.id} n={n} fmt={fmt} onSaveNote={onSaveNote} onReview={onReview} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LedgerRow({ n, fmt, onSaveNote, onReview }) {
  const [note, setNote] = useState(n.note || "");
  return (
    <tr data-testid={`ledger-row-${n.id}`} className="border-b border-[color:var(--tcm-navy-15)] align-top">
      <td className="py-3 pr-3 whitespace-nowrap text-[11px] text-[color:var(--tcm-navy-70)]">
        {n.at ? new Date(n.at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
      </td>
      <td className="py-3 pr-3">
        <span className="tcm-eyebrow" style={{ color: "var(--tcm-gold-700)" }}>{n.kind === "vendor" ? "Vendor" : "TCM"}</span>
      </td>
      <td className="py-3 pr-3">
        <span className="font-display text-[14px] block">{n.client_name}</span>
        <span className="text-[11px] text-[color:var(--tcm-navy-70)]">{n.supplier || n.label}</span>
      </td>
      <td className="py-3 pr-3 text-right tabular-nums whitespace-nowrap">{fmt(n.currency, n.amount)}</td>
      <td className="py-3 pr-3 min-w-[220px]">
        <textarea
          data-testid={`ledger-note-${n.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== (n.note || "") && onSaveNote(n.id, note)}
          placeholder="—"
          className="tcm-input text-[12px] py-1.5 px-2 w-full"
          rows={2}
          maxLength={500}
        />
      </td>
      <td className="py-3 pr-3 text-right">
        {n.reviewed ? (
          <span className="tcm-eyebrow" title={n.reviewed_at ? new Date(n.reviewed_at).toLocaleString("en-GB") : ""}>Reviewed</span>
        ) : (
          <button data-testid={`ledger-review-${n.id}`} onClick={() => onReview(n.id)} className="tcm-btn-outline text-[11px] py-1.5 px-2.5 whitespace-nowrap">Mark reviewed</button>
        )}
      </td>
    </tr>
  );
}

function TotalTile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}
         className="border border-[color:var(--tcm-navy-15)] p-4"
         style={accent ? { borderColor: "var(--tcm-gold)" } : {}}>
      <div className="tcm-eyebrow mb-2">{label}</div>
      <div className="font-display text-[26px] tabular-nums" style={accent ? { color: "var(--tcm-gold-700)" } : {}}>{value}</div>
    </div>
  );
}
